// frontend/src/scripts/last-page-store.ts

const STORAGE_KEY = 'pdfviewer:lastPage';
const STORE_PREFIX = 'list-page:';
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const MAX_ENTRIES = 500;

interface Entry {
  page: number;
  ts: number;
}

type Store = Record<string, Entry>;

function buildKey(containerId: string, fileName: string): string {
  return `${STORE_PREFIX}${containerId}/${fileName}`;
}

function readStore(): Store {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      return parsed as Store;
    }
    return {};
  } catch {
    return {};
  }
}

function writeStore(store: Store): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // Quota or disabled storage — silently drop.
  }
}

function pruneAndCap(store: Store): { store: Store; changed: boolean } {
  const now = Date.now();
  let changed = false;
  const fresh: Store = {};
  for (const [key, entry] of Object.entries(store)) {
    if (!key.startsWith(STORE_PREFIX)) {
      changed = true;
      continue;
    }

    if (!entry || typeof entry.page !== 'number' || typeof entry.ts !== 'number') {
      changed = true;
      continue;
    }
    if (now - entry.ts > MAX_AGE_MS) {
      changed = true;
      continue;
    }
    fresh[key] = entry;
  }

  const keys = Object.keys(fresh);
  if (keys.length > MAX_ENTRIES) {
    const sorted = keys
      .map((k) => ({ k, ts: fresh[k].ts }))
      .sort((a, b) => a.ts - b.ts);
    const toEvict = sorted.slice(0, keys.length - MAX_ENTRIES);
    for (const { k } of toEvict) {
      delete fresh[k];
    }
    changed = true;
  }

  return { store: fresh, changed };
}

export function getLastPage(containerId: string, fileName: string): number | null {
  const initial = readStore();
  const { store, changed } = pruneAndCap(initial);
  if (changed) {
    writeStore(store);
  }
  const entry = store[buildKey(containerId, fileName)];
  if (!entry) return null;
  return entry.page;
}

export function setLastPage(containerId: string, fileName: string, page: number): void {
  if (!Number.isInteger(page) || page < 1) return;
  const { store } = pruneAndCap(readStore());
  store[buildKey(containerId, fileName)] = { page, ts: Date.now() };
  writeStore(store);
}
